import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { aggregateReactions, RawReaction } from './team-inbox-helpers';

interface MessageReactionsProps {
    reactions?: RawReaction[] | null;
    /** Direction of the parent bubble — decides which bottom corner the pill hugs. */
    fromMe: boolean;
}

/**
 * WhatsApp-style reactions pill: a small rounded chip that overlaps the bottom
 * edge of the message bubble, showing up to 3 distinct emojis plus the total
 * count (when >1). Anchored at the bubble's bottom-LEFT for own messages and
 * bottom-RIGHT for received messages, matching WhatsApp Web.
 *
 * Positioned absolutely; the parent bubble must be `position: relative` and
 * reserve extra bottom margin (see team-inbox-message) so the pill doesn't cover
 * the following row.
 */
const MessageReactions: React.FC<MessageReactionsProps> = ({ reactions, fromMe }) => {
    const { groups, total } = useMemo(() => aggregateReactions(reactions), [reactions]);

    if (groups.length === 0) return null;

    const distinctEmojis = groups.slice(0, 3).map(g => g.emoji);
    const tooltipText = groups
        .map(g => `${g.emoji} ${g.count}`)
        .join('   ');

    return (
        <Tooltip title={tooltipText} arrow placement="top">
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '-11px',
                    // Own bubbles → hug the inner (screen-center) bottom-left corner;
                    // received bubbles → bottom-right. Uses physical left/right (not
                    // logical) because it mirrors the bubble's screen-side, not text dir.
                    ...(fromMe ? { left: '6px' } : { right: '6px' }),
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '1px',
                    height: '20px',
                    padding: '0 5px',
                    borderRadius: '11px',
                    backgroundColor: 'var(--wa-reaction-bg, var(--whatsapp-bubble-received, #fff))',
                    border: '1px solid var(--wa-reaction-border, rgba(0,0,0,0.08))',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                    fontSize: '13px',
                    lineHeight: 1,
                    cursor: 'default',
                    zIndex: 2,
                    userSelect: 'none',
                }}
            >
                {distinctEmojis.map((emoji, i) => (
                    <span key={`${emoji}-${i}`} style={{ fontSize: '13px', lineHeight: '20px' }}>
                        {emoji}
                    </span>
                ))}
                {total > 1 && (
                    <span
                        style={{
                            fontSize: '12px',
                            marginInlineStart: '2px',
                            color: 'var(--wa-meta-text, #667781)',
                            fontWeight: 500,
                        }}
                    >
                        {total}
                    </span>
                )}
            </Box>
        </Tooltip>
    );
};

export default React.memo(MessageReactions);
